// ============================================================
// useCalculator — Central state hook for the entire calculator
// ============================================================
import { useState, useCallback, useMemo, useEffect } from 'react';
import { SECTIONS, DB } from '../data/ingredients';
import { buildInitialSections, normalizeSections } from '../data/methods';
import {
  toGrams, calcNutrition, calcTotals, calcCasing, genId, emptyRow,
  buildInitialIngredients, isCasingRow, rowFeet,
  solveMeatWeightForCooked, formulaTotalPct, savedNumber, savedIndex
} from '../utils/calculations';
import { CASING_DB } from '../data/ingredients';

// Working copy of the method steps for the recipe currently being edited, so a
// reload doesn't discard them before the recipe is saved.
const METHODS_KEY = 'sc_methods';

function loadWorkingMethods() {
  try {
    const raw = localStorage.getItem(METHODS_KEY);
    if (raw) return normalizeSections(JSON.parse(raw));
  } catch (e) { /* fall through to defaults */ }
  return buildInitialSections();
}

function saveWorkingMethods(sections) {
  try {
    localStorage.setItem(METHODS_KEY, JSON.stringify(sections));
  } catch (e) { /* storage full or blocked — editing still works in-session */ }
}

// Alias for clarity
const buildInitialRows = buildInitialIngredients;

// Recipes saved before casings became a length carry a meaningless percentage on
// the casing row, which used to inflate the formula and stuffable weight. Drop
// the percentage and give the row the feet field it now needs; there is no
// sensible pct -> feet conversion, so the length starts blank.
function migrateRow(row) {
  if (!isCasingRow(row)) return row;
  return { ...row, pct: '', feet: row.feet ?? '' };
}

export function useCalculator(ingDB) {
  // ── Recipe metadata ────────────────────────────────────────
  const [recipeName,    setRecipeName]    = useState('');
  const [currentRecipeId, setCurrentRecipeId] = useState(null);  // null = unsaved new recipe
  const [cookMethod,    setCookMethod]    = useState('Hot Smoked');
  const [unitSystem,    setUnitSystem]    = useState('Metric (g)');
  const [meatWeight,    setMeatWeight]    = useState(1000);
  const [sausageStyle,  setSausageStyle]  = useState('Smoked Sausage (generic)');
  const [rating,        setRating]        = useState('');
  const [recipeNotes,   setRecipeNotes]   = useState('');
  const [equipLoss,           setEquipLoss]           = useState(150);
  // Smoker loss used by Formula Totals. Separate from the Pricing modal's yield
  // figure on purpose — this one is a formulating aid, not a costing input.
  const [cookLossPct,         setCookLossPct]         = useState(25);
  // Target finished weight. When locked, the meat+fat basis is solved from this
  // and re-solved whenever the formula or either loss changes, so the target
  // survives a whole formulating session instead of drifting as rows are added.
  const [targetCooked,        setTargetCooked]        = useState('');
  const [targetUnit,          setTargetUnit]          = useState('Imperial (lbs)');
  const [targetLocked,        setTargetLocked]        = useState(false);
  const [typicalLinkWeightG,  setTypicalLinkWeightG]  = useState(120);  // grams per link, used for sausage count scaling

  // ── Casing calculator ──────────────────────────────────────
  const [selectedCasing, setSelectedCasing] = useState(null);
  const [linkLength,     setLinkLength]     = useState(6);
  const [linkWeight,     setLinkWeight]     = useState('');
  // Real-world ft/lb override. Lives here (not in the sidebar) so the ingredient
  // grid, PDF export and pricing all read the same casing estimate, and so it
  // travels with the saved recipe.
  const [userFtPerLb,    setUserFtPerLb]    = useState('');

  // ── Ingredient rows ────────────────────────────────────────
  const [rows, setRows] = useState(buildInitialRows);
  const [pendingSections, setPendingSections] = useState(null); // set by importRecipe

  // ── Method steps ───────────────────────────────────────────
  // Saved as part of the recipe; mirrored to localStorage while unsaved.
  const [methodSections, setMethodSectionsState] = useState(loadWorkingMethods);

  const setMethodSections = useCallback((next) => {
    setMethodSectionsState(prev => {
      const value = typeof next === 'function' ? next(prev) : next;
      saveWorkingMethods(value);
      return value;
    });
  }, []);

  // ── Derived: meat weight in grams ─────────────────────────
  const meatWeightG = useMemo(
    () => toGrams(meatWeight, unitSystem),
    [meatWeight, unitSystem]
  );

  // ── Derived: flat list of filled ingredients ───────────────
  // Casings are excluded: they are priced by length, not percentage, and must
  // not reach anything that sums weights or percentages.
  const filledIngredients = useMemo(
    () => rows.filter(r => !isCasingRow(r) && r.name && r.pct),
    [rows]
  );

  // ── Derived: casing lines (name + feet), priced per foot ──
  const casingLines = useMemo(
    () => rows.filter(r => isCasingRow(r) && r.name && rowFeet(r) > 0),
    [rows]
  );

  // ── Derived: totals ────────────────────────────────────────
  const totals = useMemo(
    () => calcTotals([...filledIngredients, ...casingLines], meatWeightG,
                     equipLoss, cookLossPct),
    [filledIngredients, casingLines, meatWeightG, equipLoss, cookLossPct]
  );

  // ── Target finished weight -> meat+fat basis ──────────────
  // Percentages do not depend on the basis, so solving for it cannot feed back
  // into its own inputs; this settles in one pass.
  const totalPct = useMemo(
    () => formulaTotalPct(filledIngredients),
    [filledIngredients]
  );

  const solvedBasisG = useMemo(() => {
    if (!targetLocked) return 0;
    return solveMeatWeightForCooked(
      toGrams(targetCooked, targetUnit), totalPct, equipLoss, cookLossPct
    );
  }, [targetLocked, targetCooked, targetUnit, totalPct, equipLoss, cookLossPct]);

  useEffect(() => {
    if (!targetLocked || solvedBasisG <= 0) return;
    const next = unitSystem === 'Imperial (lbs)'
      ? Math.round(solvedBasisG / 453.592 * 1000) / 1000
      : Math.round(solvedBasisG * 10) / 10;
    setMeatWeight(prev => (parseFloat(prev) === next ? prev : next));
  }, [targetLocked, solvedBasisG, unitSystem]);

  // Typing in the basis field releases the lock — the number the user just
  // typed should stand, rather than being overwritten on the next render.
  const setMeatWeightManual = useCallback((value) => {
    setTargetLocked(false);
    setMeatWeight(value);
  }, []);

  // ── Derived: casing calculator result ─────────────────────
  // Single source of truth — the sidebar, the ingredient grid's "use estimate"
  // button, the PDF and the pricing model all read this one object.
  const casingResult = useMemo(() => {
    const entry = selectedCasing !== null ? CASING_DB[selectedCasing] : null;
    if (!entry) return null;
    // A user-edited ft/lb in the Ingredient Database wins over the shipped value
    const liveEntry = ingDB?.db?.casing?.find(c => c.name === entry[0]) || null;
    const override  = parseFloat(userFtPerLb) > 0
      ? parseFloat(userFtPerLb)
      : (liveEntry?.typMin || null);
    return calcCasing(
      entry, totals?.stuffable || 0, linkLength,
      parseFloat(linkWeight) || null, override, cookLossPct
    );
  }, [selectedCasing, totals, linkLength, linkWeight, userFtPerLb, cookLossPct, ingDB]);

  // ── Derived: nutrition ────────────────────────────────────
  const nutrition = useMemo(
    () => calcNutrition(filledIngredients, meatWeightG, ingDB),
    [filledIngredients, meatWeightG, ingDB]
  );

  // ── Row update handlers ────────────────────────────────────
  const updateRow = useCallback((id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  }, []);

  const addRow = useCallback((sectionKey) => {
    setRows(prev => {
      // Find last row of this section and insert after it
      const lastIdx = prev.reduceRight((found, r, i) =>
        found === -1 && r.sectionKey === sectionKey ? i : found, -1);
      const newRow = emptyRow(sectionKey);
      const next = [...prev];
      next.splice(lastIdx + 1, 0, newRow);
      return next;
    });
  }, []);

  const removeRow = useCallback((id) => {
    setRows(prev => {
      // Don't allow removing if it's the only row in its section
      const row = prev.find(r => r.id === id);
      if (!row) return prev;
      const sectionRows = prev.filter(r => r.sectionKey === row.sectionKey);
      if (sectionRows.length <= 1) return prev;
      return prev.filter(r => r.id !== id);
    });
  }, []);

  const clearInputs = useCallback(() => {
    setRows(buildInitialRows());
    setRecipeName('');
    setRecipeNotes('');
    setRating('');
    setCurrentRecipeId(null);
    setMethodSections(buildInitialSections());
  }, [setMethodSections]);

  // Import a recipe from the importer — sets all state atomically
  const importRecipe = useCallback((payload) => {
    const { name, notes, rows: importRows, sections } = payload;

    // Build a fresh row set starting from blank initial rows
    const fresh = buildInitialRows();

    // Build a map of section → array of filled rows to inject
    const bySection = {};
    (importRows || []).forEach(row => {
      if (!row.sect || !row.name) return;
      if (!bySection[row.sect]) bySection[row.sect] = [];
      bySection[row.sect].push(row);
    });

    // For each section, fill empty slots first, then add new ones
    const finalRows = [...fresh];
    Object.entries(bySection).forEach(([sect, toAdd]) => {
      toAdd.forEach(importRow => {
        // Find first empty slot in this section
        const emptyIdx = finalRows.findIndex(r => r.sectionKey === sect && !r.name);
        if (emptyIdx !== -1) {
          finalRows[emptyIdx] = {
            ...finalRows[emptyIdx],
            name: importRow.name,
            pct:  importRow.pct  || '',
            note: importRow.note || '',
          };
        } else {
          // No empty slot — append a new row
          finalRows.push({
            id:         genId(),
            sectionKey: sect,
            name:       importRow.name,
            pct:        importRow.pct  || '',
            note:       importRow.note || '',
          });
        }
      });
    });

    // Set everything in one render cycle
    setRows(finalRows);
    setRecipeName(name || '');
    setRecipeNotes(notes || '');
    setCurrentRecipeId(null);
    setRating('');
    if (sections && sections.length > 0) setPendingSections(sections);
  }, []);

  // ── Save / load (localStorage) ────────────────────────────
  // Save modes: 'new' (always creates), 'overwrite' (updates currentRecipeId), 'saveAs' (new with new name)
  const saveRecipe = useCallback((name, mode) => {
    const all = JSON.parse(localStorage.getItem('sc_recipes') || '[]');
    const data = {
      savedAt: new Date().toISOString(),
      recipeName: name || recipeName || 'Untitled Recipe',
      cookMethod, unitSystem, meatWeight, sausageStyle, rating,
      recipeNotes, equipLoss, cookLossPct, typicalLinkWeightG,
      targetCooked, targetUnit, targetLocked,
      selectedCasing, linkLength, linkWeight, userFtPerLb,
      // A casing row carries feet and no pct — keep it or the casing is lost
      rows: rows.filter(r => r.name || r.pct || r.note || rowFeet(r) > 0),
      methodSections,
    };

    if (mode === 'overwrite' && currentRecipeId) {
      // Update existing entry in place
      const idx = all.findIndex(r => r.id === currentRecipeId);
      if (idx !== -1) {
        all[idx] = { ...all[idx], ...data };
        localStorage.setItem('sc_recipes', JSON.stringify(all));
        setRecipeName(data.recipeName);
        return all[idx];
      }
    }
    // New entry (mode === 'new' or 'saveAs' or overwrite fallback)
    const recipe = { id: genId(), ...data };
    all.push(recipe);
    localStorage.setItem('sc_recipes', JSON.stringify(all));
    setCurrentRecipeId(recipe.id);
    setRecipeName(data.recipeName);
    return recipe;
  }, [recipeName, cookMethod, unitSystem, meatWeight, sausageStyle,
      rating, recipeNotes, equipLoss, cookLossPct, typicalLinkWeightG,
      targetCooked, targetUnit, targetLocked, selectedCasing, linkLength,
      linkWeight, userFtPerLb, rows, methodSections, currentRecipeId]);

  const renameRecipe = useCallback((id, newName) => {
    const all = JSON.parse(localStorage.getItem('sc_recipes') || '[]');
    const idx = all.findIndex(r => r.id === id);
    if (idx !== -1) {
      all[idx].recipeName = newName;
      localStorage.setItem('sc_recipes', JSON.stringify(all));
      if (id === currentRecipeId) setRecipeName(newName);
    }
  }, [currentRecipeId]);

  const loadRecipe = useCallback((recipe) => {
    setRecipeName(recipe.recipeName || '');
    setCookMethod(recipe.cookMethod || 'Hot Smoked');
    setUnitSystem(recipe.unitSystem || 'Metric (g)');
    // A stored 0 is a real value the cook typed, not a missing field. `||`
    // turned it into the default and silently changed the recipe.
    setMeatWeight(savedNumber(recipe.meatWeight, 1000));
    setSausageStyle(recipe.sausageStyle || '');
    setRating(recipe.rating || '');
    setRecipeNotes(recipe.recipeNotes || '');
    setEquipLoss(savedNumber(recipe.equipLoss, 150));
    // Recipes saved before cook loss existed fall back to the 25% working figure
    setCookLossPct(recipe.cookLossPct ?? 25);
    setTargetCooked(recipe.targetCooked ?? '');
    setTargetUnit(recipe.targetUnit || 'Imperial (lbs)');
    setTargetLocked(Boolean(recipe.targetLocked));
    setTypicalLinkWeightG(recipe.typicalLinkWeightG || 120);
    // Casing index 0 (Natural Sheep 19-21mm) is falsy, so `||` dropped the
    // selection entirely. Only a genuinely absent value means "no casing".
    setSelectedCasing(savedIndex(recipe.selectedCasing));
    setLinkLength(savedNumber(recipe.linkLength, 6));
    setLinkWeight(recipe.linkWeight || '');
    setUserFtPerLb(recipe.userFtPerLb || '');
    setCurrentRecipeId(recipe.id || null);
    // Recipes saved before methods were stored have none — fall back to defaults
    setMethodSections(normalizeSections(recipe.methodSections));

    // Merge saved rows back into full section layout
    const fresh = buildInitialRows();
    if (recipe.rows && recipe.rows.length > 0) {
      const bySect = {};
      recipe.rows.forEach(r => {
        if (!bySect[r.sectionKey]) bySect[r.sectionKey] = [];
        bySect[r.sectionKey].push(r);
      });
      const merged = [];
      const sectKeys = [...new Set(fresh.map(r => r.sectionKey))];
      sectKeys.forEach(key => {
        const savedSectRows = bySect[key] || [];
        const freshSectRows = fresh.filter(r => r.sectionKey === key);
        // Use saved rows; pad with fresh empty rows if fewer than defaults
        const combined = savedSectRows.map(r => ({ ...migrateRow(r), id: genId() }));
        const needed   = Math.max(0, freshSectRows.length - combined.length);
        for (let i = 0; i < needed; i++) combined.push(emptyRow(key));
        merged.push(...combined);
      });
      setRows(merged);
    } else {
      setRows(fresh);
    }
  }, [setMethodSections]);

  const getSavedRecipes = useCallback(() => {
    return JSON.parse(localStorage.getItem('sc_recipes') || '[]');
  }, []);

  const deleteRecipe = useCallback((id) => {
    const saved = JSON.parse(localStorage.getItem('sc_recipes') || '[]');
    localStorage.setItem('sc_recipes', JSON.stringify(saved.filter(r => r.id !== id)));
  }, []);

  return {
    // State
    recipeName, setRecipeName,
    cookMethod, setCookMethod,
    unitSystem, setUnitSystem,
    meatWeight, setMeatWeight,
    sausageStyle, setSausageStyle,
    rating, setRating,
    recipeNotes, setRecipeNotes,
    methodSections, setMethodSections,
    equipLoss, setEquipLoss,
    cookLossPct, setCookLossPct,
    targetCooked, setTargetCooked,
    targetUnit, setTargetUnit,
    targetLocked, setTargetLocked,
    totalPct, solvedBasisG,
    setMeatWeightManual,
    typicalLinkWeightG, setTypicalLinkWeightG,
    selectedCasing, setSelectedCasing,
    linkLength, setLinkLength,
    linkWeight, setLinkWeight,
    userFtPerLb, setUserFtPerLb,
    rows, updateRow, addRow, removeRow,
    // Derived
    meatWeightG,
    filledIngredients,
    casingLines,
    totals,
    nutrition,
    casingResult,
    // Actions
    clearInputs,
    importRecipe,
    pendingSections, setPendingSections,
    saveRecipe,
    renameRecipe,
    loadRecipe,
    getSavedRecipes,
    deleteRecipe,
    currentRecipeId,
  };
}
